import tw from 'twin.macro'
import React, { useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { CgTrash, CgPen } from 'react-icons/cg'
import { isEmpty, orderBy, startCase, times } from 'lodash'

import { useAlert } from 'common/components/Alert/hooks/useAlert'
import {
  useListenStripeResponseQuery,
  useSetCouponMutation,
  useSetStripeRequestMutation,
} from 'modules/ProductInfo/productInfoApi'

import { createUID } from 'common/utils/createUID'
import {
  getCouponDurationText,
  getIsCouponActive,
  getIsCouponUpcoming,
  showDiscountByType,
} from 'modules/ProductInfo/utils/utils'
import { FormRefsControlProvider } from 'common/components/RefsControl/FormRefsControl/context'
import { Button } from 'common/components/Button/Button'
import { Spinner } from 'common/components/Spinner/Spinner'
import CopyToClipboardButton from 'common/components/CopyToClipboardButton/CopyToClipboardButton'
import { Dialog, DialogContent, DialogTrigger } from 'common/components/Dialog/Dialog'
import { DeleteDialogBanner } from 'common/components/DeleteDialogBanner/DeleteDialogBanner'
import { getStandardizedName } from 'common/utils/stringUtils'
import { EditCouponForm } from '../CouponForm/EditCouponForm'
import { Pagination } from 'common/components/Pagination/Pagination'
import { usePagination } from 'common/components/Pagination/hooks/usePagination'
import { Skeleton } from 'common/components/Skeleton/Skeleton'
import TableHead from './TableHead'
import CouponExpiredCell from './CouponExpiredCell'

const COLUMNS = ['name', 'type', 'discount', 'duration']

export default function CouponsList({
  coachOrgId,
  coupons,
  plans,
  products,
  currency,
  searchQuery,
  setAddCouponDialogOpen,
  loading,
}) {
  const [editDialogId, setEditDialogId] = useState(false)

  // Coupon ids that are added to at least one plan's or product's promos
  const appliedCouponIds = useMemo(() => {
    const ids = new Set()

    ;[...Object.values(plans || {}), ...Object.values(products || {})].forEach((item) => {
      item?.promo?.forEach((prm) => {
        if (prm?.stripeCouponId) {
          ids.add(prm.stripeCouponId)
        }
      })
    })

    return ids
  }, [plans, products])

  const filteredCouponEntries = Object.entries(coupons).filter(([couponId, coupon]) => {
    const stdSearchQry = getStandardizedName(searchQuery) || ''
    const stdCouponName = getStandardizedName(coupon?.name) || ''
    const stdCouponId = getStandardizedName(coupon?.id) || ''

    return stdCouponName.includes(stdSearchQry) || stdCouponId.includes(stdSearchQry)
  })
  const filteredCoupons = Object.fromEntries(filteredCouponEntries)
  const sortedCoupons = orderBy(filteredCoupons, (coupon) => coupon?.createdAt ?? 0, 'desc')

  const totalCoupons = filteredCouponEntries.length
  const { setCurrentPage, currentPage, pageLimit, totalPages, offset } = usePagination({
    itemCount: totalCoupons,
    initialLimit: 20,
  })

  const currPageCoupons = sortedCoupons.slice(offset, currentPage * pageLimit)

  if (loading) {
    return (
      <div>
        <div>
          <div className='invisible'>
            <Pagination
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              pageLimit={pageLimit}
              itemCount={totalCoupons}
              totalPages={totalPages}
            />
          </div>
          <div className='overflow-x-auto'>
            <div className='align-middle inline-block min-w-full'>
              <div className='relative overflow-hidden border-b border-gray-200 rounded-xl' data-testid='table-wrapper'>
                <table className='min-w-full divide-y divide-gray-200 table-fixed'>
                  <TableHead columns={COLUMNS} />
                  <tbody className='bg-white divide-y divide-gray-200 z-50'>
                    {times(20, (idx) => (
                      <tr key={idx} className='relative h-20'>
                        <td className='px-2 md:px-6 py-3 table-cell w-2/5'>
                          <Skeleton className='w-1/3' />
                        </td>
                        <td className='px-2 md:px-6 py-3 table-cell w-1/5'>
                          <Skeleton />
                        </td>
                        <td className='px-2 md:px-6 py-3 table-cell w-1/5'>
                          <Skeleton />
                        </td>
                        <td className='px-2 md:px-6 py-3 table-cell w-1/5'>
                          <Skeleton />
                        </td>
                        <td className='px-2 md:px-6 py-3 table-cell w-1/5' />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className='invisible'>
            <Pagination
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              pageLimit={pageLimit}
              itemCount={totalCoupons}
              totalPages={totalPages}
            />
          </div>
        </div>
      </div>
    )
  }

  if (isEmpty(coupons)) {
    return (
      <div className='flex flex-col items-center justify-center h-96 bg-white px-6 py-4 rounded-xl'>
        <h3 className='font-bold text-2xl text-tBlack'>No coupons</h3>
        <div className='mt-4'>
          <Button size='md' onClick={() => setAddCouponDialogOpen(true)}>
            Add new coupon
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div>
      {isEmpty(filteredCoupons) ? (
        <div className='flex flex-col items-center justify-center h-96 bg-white px-6 py-4 rounded-xl'>
          <h3 className='font-bold text-2xl text-tBlack'>No coupons for this search</h3>
        </div>
      ) : (
        <div>
          <Pagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            pageLimit={pageLimit}
            itemCount={totalCoupons}
            totalPages={totalPages}
          />
          <div className='overflow-x-auto'>
            <div className='align-middle inline-block min-w-full'>
              <div className='relative overflow-hidden border-b border-gray-200 rounded-xl' data-testid='table-wrapper'>
                <table className='min-w-full divide-y divide-gray-200 table-fixed'>
                  <TableHead columns={COLUMNS} />
                  <tbody className='bg-white divide-y divide-gray-200 z-50'>
                    {currPageCoupons.map((coupon) => (
                      <CouponListItem
                        key={coupon.id}
                        coachOrgId={coachOrgId}
                        coupon={coupon}
                        currency={currency}
                        isAppliedToPlan={appliedCouponIds.has(coupon.id)}
                        editDialogId={editDialogId}
                        setEditDialogId={setEditDialogId}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <Pagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            pageLimit={pageLimit}
            itemCount={totalCoupons}
            totalPages={totalPages}
          />
        </div>
      )}
    </div>
  )
}

function CouponListItem({ coachOrgId, coupon, currency, isAppliedToPlan, editDialogId, setEditDialogId }) {
  const { createAlert } = useAlert()

  const actionContainerRef = useRef(null)
  const copyIdButtonRef = useRef(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const [setCoupon] = useSetCouponMutation()
  const [setStripeRequest] = useSetStripeRequestMutation()

  const { data: stripeResponse } = useListenStripeResponseQuery({ coachOrgId, responseId: coupon?.id })
  const stripeResponsePending = stripeResponse?.status === 'pending'
  const errorMessage = stripeResponse?.error

  const isCouponActive = getIsCouponActive({ coupon })
  const isCouponUpcoming = getIsCouponUpcoming({ coupon })

  const isCouponExpired = !isCouponActive && !isCouponUpcoming

  // Coupons don't auto-apply to plans on creation, so nudge the coach to add it to a plan/product
  const showApplyHint = !isAppliedToPlan && !isCouponExpired && !errorMessage && !stripeResponsePending

  const handleDeleteCoupon = async () => {
    const payload = {
      couponId: coupon?.id,
      stripeCouponId: coupon?.id,
    }

    if (coupon?.type === 'trialOnly') {
      await setCoupon({ coachOrgId, couponKey: coupon?.id, coupon: null })
    } else {
      const requestId = createUID()
      // Awaited, because removing a coupon is entirely the backend's doing and this request
      // is the only thing that asks for it. Left in flight it can be cancelled by the coach
      // navigating away, and then the coupon stays in the org for good while the interface
      // has already said "Coupon deleted!".
      await setStripeRequest({
        requestId,
        coachOrgId,
        type: 'DELETE_COUPON',
        payload,
      })
    }

    createAlert({ text: `Coupon deleted!`, type: 'success' })
  }

  const handleEditCoupon = async ({ formData, coupon }) => {
    const payload = {
      stripeCouponId: coupon.id,
      name: formData.name,
    }
    // If name did not change or trialOnly coupon, only firebase update is enough
    if (coupon.type === 'trialOnly' || coupon.name === formData.name) {
      await setCoupon({ coachOrgId, couponKey: coupon.id, coupon: { ...coupon, ...formData } })
    } else {
      await setCoupon({ coachOrgId, couponKey: coupon.id, coupon: { ...coupon, ...formData, stripeStatus: 'pending' } })
      const requestId = createUID()
      // Awaited for the same reason as the create and delete requests: the edit dialog closes
      // straight after this, and a request still in flight can be cancelled before it is
      // written. The coupon would keep its old name in Stripe while the dashboard showed the
      // new one, and its status would sit at "pending" for good.
      await setStripeRequest({
        requestId,
        coachOrgId,
        type: 'UPDATE_COUPON',
        payload,
      })
    }
    setEditDialogId(false)
    createAlert({ text: `Coupon updated!`, type: 'success' })
  }

  const handleErrRetry = async () => {
    const payload = stripeResponse?.data?.payload
    const type = stripeResponse?.type
    const requestId = createUID()
    await setStripeRequest({
      requestId,
      coachOrgId,
      type,
      payload,
    })
    createAlert({ text: 'Retrying...', type: 'success' })
  }

  const handleErrDelete = async () => {
    await setCoupon({ coachOrgId, couponKey: coupon.id, coupon: null })
    createAlert({ text: 'Coupon deleted!', type: 'success' })
  }

  const handleRowClick = (e) => {
    if (Boolean(errorMessage)) {
      return
    }

    const occuredOutsideModal = e.currentTarget.contains(e.target)

    // Check that event did not happen in action row (edit/delete)
    const occuredOutsideActionRow = !actionContainerRef.current.contains(e.target)
    const occuredOutsideCopyButton = !copyIdButtonRef.current.contains(e.target)

    if (occuredOutsideModal && occuredOutsideActionRow && occuredOutsideCopyButton) {
      setEditDialogId(coupon.id)
    }
  }

  return (
    <tr
      className='relative h-20 cursor-pointer group hover:bg-gray-50'
      css={(Boolean(errorMessage) || showApplyHint) && tw`h-[100px]`}
      onClick={handleRowClick}
    >
      <td className='px-2 md:px-6 py-3 table-cell w-2/5'>
        <div className='flex flex-col'>
          <span className='font-medium text-black mb-1'>{coupon?.name || 'Untitled Coupon'}</span>
          <div className='flex items-center'>
            <span className='text-sm text-black'>{`Code: ${coupon.id}` || '(couponid-autogenerated)'}</span>
            <CopyToClipboardButton
              buttonRef={copyIdButtonRef}
              data={coupon.id || '(couponid-autogenerated)'}
              customClassName='ml-2 cursor-pointer text-black group-hover:visible group-hover:pointer-events-auto invisible pointer-events-none hover:text-tGreen'
              iconClassName='w-3.5 h-3.5'
            />
          </div>
        </div>
      </td>
      <td className='px-2 md:px-6 py-3 table-cell w-1/5'>
        <span className='text-sm text-black'>{startCase(coupon.type)}</span>
      </td>
      <td className='px-2 md:px-6 py-3 table-cell w-1/5'>
        <span className='text-sm text-black'>
          {showDiscountByType({ type: coupon.type, discount: coupon.discount, currency })}
        </span>
      </td>
      <td className='px-2 md:px-6 py-3 table-cell w-1/5'>
        <span className='text-sm text-black'>{getCouponDurationText({ coupon })}</span>
      </td>
      <CouponExpiredCell isCouponActive={isCouponActive} isCouponUpcoming={isCouponUpcoming} />
      <td
        ref={actionContainerRef}
        className='absolute top-1/2 -translate-y-1/2 right-3 inline-flex items-center justify-end bg-white group-hover:border group-hover:border-tBlack group-hover:border-opacity-20 rounded-lg group-hover:shadow-sm'
        css={[Boolean(errorMessage) && tw`invisible pointer-events-none`]}
      >
        {stripeResponsePending ? (
          <Spinner />
        ) : (
          <>
            <button
              aria-label={`Edit ${coupon.name || 'Untitled'} coupon`}
              onClick={() => setEditDialogId(coupon.id)}
              className='hidden group-hover:inline-block hover:text-tGreen py-1 px-2 border-r border-tBlack border-opacity-20 transition-all disabled:cursor-not-allowed'
            >
              <CgPen className=' w-5 h-5' />
            </button>
            <Dialog open={editDialogId === coupon.id} setOpen={setEditDialogId}>
              <DialogContent header='Edit coupon'>
                <FormRefsControlProvider>
                  <EditCouponForm coupon={coupon} handleEditCoupon={handleEditCoupon} />
                </FormRefsControlProvider>
              </DialogContent>
            </Dialog>
            <Dialog open={deleteDialogOpen} setOpen={setDeleteDialogOpen}>
              <DialogTrigger
                aria-label={`Delete ${coupon.name || 'Untitled'} coupon`}
                className='hidden group-hover:inline-block hover:text-tRed py-1 px-2 transition-all disabled:cursor-not-allowed'
                onOpenCb={(e) => e.stopPropagation()}
              >
                <CgTrash className='w-5 h-5' />
              </DialogTrigger>
              <DialogContent>
                <DeleteDialogBanner
                  text={`This will delete the ${coupon.name || 'Untitled'} coupon`}
                  handleDelete={handleDeleteCoupon}
                />
              </DialogContent>
            </Dialog>
          </>
        )}
      </td>
      {Boolean(errorMessage) && (
        <div className='absolute bottom-0 inset-x-0 text-sm mt-1 max-w-[90%] px-2 md:px-6 py-1 z-10'>
          <span className='text-tRed'>{errorMessage}</span>
          <button
            type='button'
            className='ml-2 underline hover:text-tGreen transition-colors'
            onClick={() => handleErrRetry()}
          >
            Retry
          </button>
          <span className='mx-2'>or</span>
          <button
            type='button'
            className='underline hover:text-tRed transition-colors'
            onClick={() => handleErrDelete()}
          >
            Delete coupon
          </button>
        </div>
      )}
      {showApplyHint && (
        <div className='absolute bottom-0 left-0 text-xs mt-1 px-2 md:px-6 py-1 z-10'>
          <span className='text-gray-500'>To activate, add this coupon to at least one </span>
          <Link
            to='/sell/pricing'
            className='underline text-tGreen hover:opacity-80 transition-opacity'
            onClick={(e) => e.stopPropagation()}
          >
            plan
          </Link>
          <span className='text-gray-500'> or </span>
          <Link
            to='/sell'
            className='underline text-tGreen hover:opacity-80 transition-opacity'
            onClick={(e) => e.stopPropagation()}
          >
            product
          </Link>
        </div>
      )}
    </tr>
  )
}
